import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { UserService } from "../../services/User.service";

const ResetPasswordPage: React.FC = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const validateToken = async () => {
      const token = searchParams.get("token");
      if (!token) {
        setError("El token no está presente en la URL.");
        setValidatingToken(false);
        return;
      }

      try {
        const validate = await UserService.validateToken(token);
        setIsTokenValid(true);
      } catch {
        setError("El token recibido es inválido.");
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [searchParams]);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    const token = searchParams.get("token");
    if (!token) {
      setError("Token no válido o ausente.");
      return;
    }

    setLoading(true);

    try {
      await UserService.updatePasswordWithToken(token, newPassword);
      setMessage("Contraseña actualizada con éxito.");
      setTimeout(() => navigate("/login"), 3000); // Redirige después de 3 segundos
    } catch (err) {
      setError("No se pudo actualizar la contraseña. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div style={styles.container}>
        <div style={styles.formContainer}>
          <h1 style={styles.title}>Validando token...</h1>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.formContainer}>
        {isTokenValid ? (
          <>
            <h1 style={styles.title}>Restablecer Contraseña</h1>
            {error && <p style={styles.error}>{error}</p>}
            {message ? (
              <p style={styles.success}>{message}</p>
            ) : (
              <form onSubmit={handlePasswordChange} style={styles.form}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Nueva Contraseña:</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Confirmar Contraseña:</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={styles.input}
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  style={styles.buttonPrimary}
                >
                  {loading ? "Cambiando contraseña..." : "Cambiar Contraseña"}
                </button>
              </form>
            )}
          </>
        ) : (
          <p style={styles.error}>El token recibido es inválido.</p>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: "#f5f5f5",
  },
  formContainer: {
    padding: "2rem",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    textAlign: "center" as const,
  },
  title: {
    marginBottom: "1rem",
    fontSize: "24px",
    fontWeight: "bold",
  },
  form: {
    marginBottom: "1rem",
  },
  inputGroup: {
    marginBottom: "1rem",
  },
  label: {
    display: "block",
    marginBottom: "0.5rem",
    fontSize: "14px",
    fontWeight: "bold",
  },
  input: {
    width: "100%",
    padding: "0.5rem",
    borderRadius: "4px",
    border: "1px solid #ccc",
    fontSize: "14px",
  },
  buttonPrimary: {
    width: "100%",
    padding: "0.5rem",
    borderRadius: "4px",
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    fontWeight: "bold",
  },
  error: {
    color: "red",
    fontSize: "14px",
    marginTop: "1rem",
  },
  success: {
    color: "green",
    fontSize: "14px",
    marginTop: "1rem",
  },
};

export default ResetPasswordPage;
